/**
 * Speaker profiles -- group 00 (reference exemplars).
 *
 * The kit derives an anechoic baseline from the published bass alignment and HF
 * limit, then applies an authored voicing offset (`responseTilt`) which is the
 * part that carries the design's actual character. Impedance is modelled from
 * the alignment so that low-feedback amplifiers interact with it correctly.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_00: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Magico',
    model: 'A5',
    price: usd(24800, 'MSRP', 2023),
    year: 2021,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'sealed',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [24, 20],
    hfExtension: 50000,
    crossovers: [[220, 24], [3000, 24]],
    responseTilt: [[20, 0], [80, 0.3], [200, -0.2], [900, 0], [2500, 0.4], [6000, -0.3], [12000, 0.2], [20000, -0.6]],
    resonances: [[340, -44, 22], [780, -48, 26]],
    coverageAngleDeg: 100,
    recommendedWatts: [50, 600],
    dimensionsMm: [1180, 267, 318],
    massKg: 49,
    thdPercent: 0.22,
    roomCoupling: 'low',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9aa4b2',
    tags: ['sealed', 'beryllium', 'aluminium-cabinet'],
    notes:
      'Sealed aluminium enclosure with a beryllium dome and Magico\'s graphene-loaded cones. The sealed alignment gives a second-order low-frequency rolloff, which the model reflects: a shallower slope below f3 than a ported design of the same extension, with correspondingly better room-gain integration and lower group delay.',
    provenanceNote:
      'Sensitivity, impedance, driver complement and bass extension from Magico published specifications. Anechoic response shape and directivity are modelled.',
  }),

  makeSpeaker({
    mfr: 'Klipsch',
    model: 'Klipschorn AK6',
    price: usd(19000, 'MSRP', 2023, 'Price per pair.'),
    year: 2019,
    country: 'United States',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 105,
    nominalOhms: 8,
    minOhms: 5.6,
    bass: [32, 26],
    hfExtension: 20000,
    crossovers: [[450, 12], [4500, 12]],
    responseTilt: [[20, 0], [60, 1.2], [120, 0.4], [400, -0.8], [900, 0.6], [2000, 1.0], [5000, -1.4], [9000, 0.5], [16000, -2.5]],
    resonances: [[145, -30, 9], [420, -34, 12], [1900, -38, 16]],
    coverageAngleDeg: 60,
    recommendedWatts: [5, 100],
    dimensionsMm: [1257, 785, 720],
    massKg: 79,
    thdPercent: 0.55,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c98c4e',
    tags: ['horn', 'corner-loaded', 'high-sensitivity', 'set-friendly'],
    notes:
      'Fully horn-loaded three-way, corner-coupled bass horn. At 105 dB/2.83 V the modelled noise contribution of the upstream chain becomes audible in a way it never is with an 86 dB speaker, and the narrow 60-degree pattern strongly reduces sidewall reflection energy in the room model. Distortion rises with SPL faster than a direct radiator because of air nonlinearity in the horn throat.',
    provenanceNote:
      'Sensitivity, crossover points and dimensions from Klipsch published specifications. The bass horn requires corner placement; the room model treats this as high room coupling.',
  }),

  makeSpeaker({
    mfr: 'MartinLogan',
    model: 'Neolith',
    price: usd(99995, 'MSRP', 2023, 'Price per pair.'),
    year: 2015,
    country: 'United States',
    speakerType: 'electrostatic',
    enclosure: 'dipole-panel',
    drivers: ['electrostatic-panel', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 90,
    nominalOhms: 4,
    minOhms: 0.8,
    bass: [23, 19],
    hfExtension: 25000,
    crossovers: [[60, 24], [400, 12]],
    responseTilt: [[20, 0], [50, 0.5], [150, -0.6], [500, 0.2], [1500, 0.5], [4000, 0.3], [10000, -0.5], [20000, -2.0]],
    coverageAngleDeg: 30,
    recommendedWatts: [100, 1300],
    dimensionsMm: [1854, 826, 587],
    massKg: 172,
    thdPercent: 0.12,
    roomCoupling: 'high',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fd0e8',
    tags: ['electrostatic', 'hybrid', 'dipole', 'difficult-load'],
    notes:
      'Hybrid electrostatic: a large curvilinear stator panel above 400 Hz crossed to sealed dynamic woofers. The impedance model is the important part -- a panel is a capacitor, so impedance falls toward 0.8 ohm at 20 kHz. Amplifiers with high output impedance will show a clearly falling treble on this load, which the engine computes from the two profiles rather than baking into either one.',
    provenanceNote:
      'Sensitivity, minimum impedance, panel dimensions and crossover points from MartinLogan published specifications. Panel impedance behaviour is modelled as a capacitive load.',
  }),

  makeSpeaker({
    mfr: 'Harbeth',
    model: 'Monitor 40.3 XD',
    price: usd(21999, 'MSRP', 2023, 'Price per pair.'),
    year: 2021,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 86,
    nominalOhms: 6,
    minOhms: 4.8,
    bass: [35, 29],
    hfExtension: 22000,
    portTuningHz: 34,
    crossovers: [[380, 12], [3500, 12]],
    responseTilt: [[20, 0], [70, -0.3], [200, 0.4], [600, 0.2], [1500, -0.4], [3000, -0.8], [6000, 0.3], [12000, -0.4], [20000, -1.6]],
    resonances: [[180, -26, 7], [420, -30, 9]],
    coverageAngleDeg: 130,
    recommendedWatts: [25, 300],
    dimensionsMm: [750, 432, 388],
    massKg: 37,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b6a48c',
    tags: ['bbc-lineage', 'thin-wall', 'radial-cone'],
    notes:
      'Thin-wall BBC-lineage cabinet: the enclosure is deliberately allowed to resonate at a low level and low Q rather than being made inert, which is modelled here as two relatively low-Q cabinet resonances in the lower midrange rather than the high-Q, high-frequency signature of a braced rigid box.',
    provenanceNote:
      'Sensitivity, impedance, dimensions and driver complement from Harbeth published specifications. Cabinet resonance behaviour is modelled from the design\'s stated thin-wall philosophy.',
  }),
];
